import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, Calendar, RotateCcw, XCircle } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from './LanguageToggle';

interface NavbarProps {
  language: string;
  setLanguage: (lang: string) => void;
}

const Navbar: React.FC<NavbarProps> = ({ language, setLanguage }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isBookingPage = location.pathname === '/book';
  const isCancellationPage = location.pathname === '/cancel-appointment';
  const isReschedulePage = location.pathname.includes('reschedule');
  const isDarkPage = isBookingPage || isCancellationPage || isReschedulePage;

  const content = {
    en: {
      links: {
        services: "Services",
        about: "About",
        testimonials: "Testimonials",
        contact: "Contact"
      },
      bookNow: "Book Now",
      cancelAppointment: "Cancel Appointment",
      dropdown: {
        book: "📅 Book Appointment",
        reschedule: "🔄 Reschedule",
        cancel: "❌ Cancel Appointment"
      }
    },
    da: {
      links: {
        services: "Behandlinger",
        about: "Om os",
        testimonials: "Anmeldelser",
        contact: "Kontakt"
      },
      bookNow: "Book nu",
      cancelAppointment: "Aflys tid",
      dropdown: {
        book: "📅 Book Tid",
        reschedule: "🔄 Ombook",
        cancel: "❌ Aflys Tid"
      }
    }
  };

  const toggleMenu = () => setIsOpen(!isOpen);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.dropdown-container')) {
        setIsDropdownOpen(false);
      }
    };

    handleResize();
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    document.addEventListener('click', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleBookClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/book');
    setIsDropdownOpen(false);
  };

  const handleCancelClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/cancel-appointment');
    setIsDropdownOpen(false);
  };

  const handleDropdownToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  };

  const NavLinks = () => (
    <>
      <Link
        to="/services"
        className={`hover:text-[#A68B7C] transition-colors font-medium ${
          isDarkPage ? 'text-white' : 'text-gray-900'
        }`}
      >
        {content[language as keyof typeof content].links.services}
      </Link>
      <Link
        to="/about"
        className={`hover:text-[#A68B7C] transition-colors font-medium ${
          isDarkPage ? 'text-white' : 'text-gray-900'
        }`}
      >
        {content[language as keyof typeof content].links.about}
      </Link>
      <Link
        to="/testimonials"
        className={`hover:text-[#A68B7C] transition-colors font-medium ${
          isDarkPage ? 'text-white' : 'text-gray-900'
        }`}
      >
        {content[language as keyof typeof content].links.testimonials}
      </Link>
      <Link
        to="/contact"
        className={`hover:text-[#A68B7C] transition-colors font-medium ${
          isDarkPage ? 'text-white' : 'text-gray-900'
        }`}
      >
        {content[language as keyof typeof content].links.contact}
      </Link>
    </>
  );

  const MobileNavLinks = ({ closeMenu }: { closeMenu: () => void }) => (
    <>
      <Link
        to="/services"
        className="text-white py-2 hover:text-[#A68B7C] transition-colors font-medium transform hover:translate-x-1"
        onClick={closeMenu}
      >
        {content[language as keyof typeof content].links.services}
      </Link>
      <Link
        to="/about"
        className="text-white py-2 hover:text-[#A68B7C] transition-colors font-medium transform hover:translate-x-1"
        onClick={closeMenu}
      >
        {content[language as keyof typeof content].links.about}
      </Link>
      <Link
        to="/testimonials"
        className="text-white py-2 hover:text-[#A68B7C] transition-colors font-medium transform hover:translate-x-1"
        onClick={closeMenu}
      >
        {content[language as keyof typeof content].links.testimonials}
      </Link>
      <Link
        to="/contact"
        className="text-white py-2 hover:text-[#A68B7C] transition-colors font-medium transform hover:translate-x-1"
        onClick={closeMenu}
      >
        {content[language as keyof typeof content].links.contact}
      </Link>
    </>
  );

  const navbarBackground = isBookingPage
    ? 'bg-black/50 backdrop-blur-md'
    : isScrolled
    ? 'bg-white/90 backdrop-blur-md shadow-sm'
    : 'bg-transparent';

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${navbarBackground}`}>
      <div className="container mx-auto px-4 md:px-6 py-4">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className={`text-2xl md:text-3xl font-serif font-bold tracking-tight ${
              isDarkPage ? 'text-white' : 'text-gray-900'
            }`}
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            BeautyEsthetics
          </Link>

          <nav className="hidden md:flex items-center space-x-6">
            <NavLinks />
            <LanguageToggle language={language} setLanguage={setLanguage} />

            {/* Book Now Dropdown */}
            <div className="relative dropdown-container">
              <button
                onClick={handleDropdownToggle}
                className="flex items-center gap-2 px-8 py-3 rounded-lg bg-gradient-to-r from-[#C0A080] to-[#A68B7C] text-white hover:from-[#D4B494] hover:to-[#B8967D] transition-all duration-300 font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
                style={{ fontFamily: "'Inter', sans-serif", fontSize: '16px', minHeight: '48px' }}
                aria-expanded={isDropdownOpen}
                aria-haspopup="true"
                aria-label={`${content[language as keyof typeof content].bookNow} menu`}
              >
                <span style={{ fontWeight: '500' }}>
                  {content[language as keyof typeof content].bookNow}
                </span>
                <ChevronDown
                  size={18}
                  className={`transition-transform duration-500 ease-in-out ${
                    isDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Dropdown Menu */}
              <div
                className={`absolute top-full right-0 mt-3 w-64 bg-gradient-to-b from-[#F5F5F0] to-white rounded-xl shadow-2xl border border-[#C0A080]/20 overflow-hidden transition-all duration-500 ease-out transform origin-top-right z-50 ${
                  isDropdownOpen
                    ? 'opacity-100 scale-100 translate-y-0 visible'
                    : 'opacity-0 scale-95 -translate-y-3 invisible pointer-events-none'
                }`}
                style={{ backdropFilter: 'blur(10px)' }}
                role="menu"
                aria-orientation="vertical"
              >
                <div className="py-3">
                  <button
                    onClick={handleBookClick}
                    className="w-full px-6 py-4 text-left text-[#1B2B3A] hover:bg-gradient-to-r hover:from-[#C0A080]/10 hover:to-[#C0A080]/5 hover:text-[#C0A080] transition-all duration-300 flex items-center gap-4 group relative overflow-hidden"
                    style={{ fontFamily: "'Inter', sans-serif", fontSize: '16px', minHeight: '56px' }}
                    role="menuitem"
                    tabIndex={0}
                  >
                    <div className="absolute left-0 top-0 h-full w-1 bg-[#C0A080] transform scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-top"></div>
                    <Calendar size={18} className="text-[#C0A080] group-hover:scale-110 transition-transform duration-300" />
                    <span
                      className="font-medium group-hover:translate-x-1 transition-transform duration-300"
                      style={{ fontWeight: '500' }}
                    >
                      {content[language as keyof typeof content].dropdown.book}
                    </span>
                  </button>

                  <div className="h-px bg-gradient-to-r from-transparent via-[#C0A080]/30 to-transparent mx-4 my-2"></div>

                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/reschedule-appointment');
                      setIsDropdownOpen(false);
                    }}
                    className="w-full px-6 py-4 text-left text-[#1B2B3A] hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-25 hover:text-blue-700 transition-all duration-300 flex items-center gap-4 group relative overflow-hidden"
                    style={{ fontFamily: "'Inter', sans-serif", fontSize: '16px', minHeight: '56px' }}
                    role="menuitem"
                    tabIndex={0}
                  >
                    <div className="absolute left-0 top-0 h-full w-1 bg-blue-500 transform scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-top"></div>
                    <RotateCcw size={18} className="text-blue-500 group-hover:scale-110 transition-transform duration-300" />
                    <span
                      className="font-medium group-hover:translate-x-1 transition-transform duration-300"
                      style={{ fontWeight: '500' }}
                    >
                      {content[language as keyof typeof content].dropdown.reschedule}
                    </span>
                  </button>

                  <div className="h-px bg-gradient-to-r from-transparent via-[#C0A080]/30 to-transparent mx-4 my-2"></div>

                  <button
                    onClick={handleCancelClick}
                    className="w-full px-6 py-4 text-left text-[#1B2B3A] hover:bg-gradient-to-r hover:from-red-50 hover:to-red-25 hover:text-red-700 transition-all duration-300 flex items-center gap-4 group relative overflow-hidden"
                    style={{ fontFamily: "'Inter', sans-serif", fontSize: '16px', minHeight: '56px' }}
                    role="menuitem"
                    tabIndex={0}
                  >
                    <div className="absolute left-0 top-0 h-full w-1 bg-red-500 transform scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-top"></div>
                    <XCircle size={18} className="text-red-500 group-hover:scale-110 transition-transform duration-300" />
                    <span
                      className="font-medium group-hover:translate-x-1 transition-transform duration-300"
                      style={{ fontWeight: '500' }}
                    >
                      {content[language as keyof typeof content].dropdown.cancel}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </nav>

          <div className="flex items-center space-x-4 md:hidden">
            <LanguageToggle language={language} setLanguage={setLanguage} />
            <button
              className={`${isDarkPage ? 'text-white' : 'text-gray-900'} relative z-50 p-2 rounded-md transition-all duration-300 ${
                isOpen && isDarkPage 
                  ? 'bg-[#A68B7C]/20 backdrop-blur-sm border border-[#A68B7C]/30' 
                  : isOpen 
                  ? 'bg-white/10 backdrop-blur-sm' 
                  : ''
              }`}
              onClick={toggleMenu}
              aria-label={isOpen ? "Close menu" : "Open menu"}
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div
          className={`fixed inset-0 backdrop-blur-lg md:hidden z-40 mobile-menu-overlay ${
            isDarkPage ? 'bg-black/80' : 'bg-black/70'
          }`}
          onClick={() => setIsOpen(false)}
        >
          <div
            className={`absolute right-0 top-0 h-full w-[80%] max-w-sm backdrop-blur-md shadow-xl p-6 pt-20 mobile-menu-content ${
              isDarkPage 
                ? 'bg-gradient-to-b from-[#1A1A1A]/98 to-[#2A2A2A]/95 border-l border-[#A68B7C]/20' 
                : 'bg-[#1A1A1A]/95'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col space-y-6">
              <MobileNavLinks closeMenu={() => setIsOpen(false)} />

              <div className="h-px bg-white/10 my-4" />

              <button
                onClick={(e) => {
                  handleBookClick(e);
                  setIsOpen(false);
                }}
                className="px-6 py-3 rounded-lg bg-gradient-to-r from-[#C0A080] to-[#A68B7C] text-white hover:from-[#D4B494] hover:to-[#B8967D] transition-all duration-300 font-medium text-center transform hover:scale-[1.02] active:scale-[0.98] shadow-lg"
                style={{ fontFamily: "'Inter', sans-serif", fontSize: '16px' }}
              >
                📅 {content[language as keyof typeof content].bookNow}
              </button>

              <button
                onClick={(e) => {
                  navigate('/reschedule-appointment');
                  setIsOpen(false);
                }}
                className="px-6 py-3 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-all duration-300 font-medium text-center transform hover:scale-[1.02] active:scale-[0.98] shadow-lg"
                style={{ fontFamily: "'Inter', sans-serif", fontSize: '16px' }}
              >
                🔄 {language === 'en' ? 'Reschedule' : 'Ombook'}
              </button>

              <button
                onClick={(e) => {
                  handleCancelClick(e);
                  setIsOpen(false);
                }}
                className="px-6 py-3 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-all duration-300 font-medium text-center transform hover:scale-[1.02] active:scale-[0.98] shadow-lg"
                style={{ fontFamily: "'Inter', sans-serif", fontSize: '16px' }}
              >
                ❌ {content[language as keyof typeof content].cancelAppointment}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;